import React, { useState, useEffect, useRef, useMemo } from "react";
import HomeCallouts from "../components/HomeCallouts";
import Section from "../components/Section";

type TypingOptions = {
  typeSpeed?: number;
  deleteSpeed?: number;
  waitBeforeDelete?: number;
  waitBeforeType?: number;
  jitter?: number;
};

const useTypingEffect = (
  phrases: string[] = [],
  {
    typeSpeed = 95,
    deleteSpeed = 55,
    waitBeforeDelete = 1400,
    waitBeforeType = 420,
    jitter = 60
  }: TypingOptions = {}
) => {
  const sanitizedPhrases = useMemo(() => phrases.filter(Boolean), [phrases]);
  const [typedText, setTypedText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentPhrase = sanitizedPhrases[phraseIndex] ?? "";

  useEffect(() => {
    if (!sanitizedPhrases.length) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!isDeleting && charIndex === currentPhrase.length) {
      timeoutRef.current = setTimeout(() => {
        setIsDeleting(true);
      }, waitBeforeDelete);
      return;
    }

    if (isDeleting && charIndex === 0) {
      timeoutRef.current = setTimeout(() => {
        setIsDeleting(false);
        setPhraseIndex((prev) => (prev + 1) % sanitizedPhrases.length);
      }, waitBeforeType);
      return;
    }

    const baseSpeed = isDeleting ? deleteSpeed : typeSpeed;
    const randomOffset = Math.floor(Math.random() * jitter) - jitter / 2;
    const delay = Math.max(35, baseSpeed + randomOffset);

    timeoutRef.current = setTimeout(() => {
      const nextIndex = charIndex + (isDeleting ? -1 : 1);
      setCharIndex(nextIndex);
      setTypedText(currentPhrase.slice(0, nextIndex));
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    sanitizedPhrases,
    phraseIndex,
    charIndex,
    isDeleting,
    typeSpeed,
    deleteSpeed,
    waitBeforeDelete,
    waitBeforeType,
    jitter
  ]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return typedText.length === currentPhrase.length ? typedText : currentPhrase;
};

export default function Home() {
  const heroPhrases = useMemo(
    () => [
      "Full-Stack Engineering",
      "TypeScript & React Specialist",
      "Backend & API Builder",
      "AI / Agent Systems Developer",
      "Automation & SecOps Partner"
    ],
    []
  );

  const typedRole = useTypingEffect(heroPhrases);

  return (
    <main className="min-h-screen text-primary">
      <Section className="pt-10 pb-2">
        <div className="space-y-10">
          <div className="space-y-6">
            <p className="font-mono text-xs uppercase tracking-[0.4em] text-text-subtle">
              systems · automation · ai
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold font-mono leading-tight tracking-tight">
              Enkae<span className="text-accent">()</span>
            </h1>
            <p className="text-base sm:text-lg text-text-muted max-w-2xl leading-relaxed">
              Building resilient software, secure automation, and intelligent agents with a security-first mindset and uncompromising attention to craft.
            </p>
            <p className="mt-4 text-sm md:text-base text-text-muted max-w-xl">
              I design and build secure automation tools, dashboards, and UIs for teams who are tired of manual workflows.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#projects"
                className="inline-flex items-center rounded-full border border-primary bg-primary px-6 py-2 font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-text-muted transition hover:bg-primary/90"
              >
                VIEW PROJECTS
              </a>
              <a
                href="#contact"
                className="inline-flex items-center rounded-full border border-border-subtle px-6 py-2 font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-text-muted transition hover:border-primary hover:text-primary"
              >
                GET IN TOUCH
              </a>
            </div>
          </div>

          <div className="bg-surface/80 border border-border-subtle rounded-card p-6 sm:p-8 shadow-card">
            <p className="font-mono text-sm text-text-subtle uppercase tracking-[0.25em] mb-4">
              current focus
            </p>
            <div className="text-2xl sm:text-3xl font-mono text-primary flex items-center">
              <span aria-hidden="true" className="text-accent mr-3">
                &gt;
              </span>
              <span aria-live="polite">{typedRole}</span>
              <span className="cursor-blink" aria-hidden="true" />
            </div>
          </div>

        </div>
      </Section>

      <Section id="projects" className="pt-0 pb-20">
        <HomeCallouts />
      </Section>
      <footer className="mt-16 border-t border-border-subtle pt-6 pb-8 text-xs text-text-subtle">
        <div className="max-w-5xl mx-auto px-4">
          <p>© {new Date().getFullYear()} Enkae. All rights reserved.</p>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <a
              href="https://github.com/enkae-code"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary underline-offset-2 hover:underline"
            >
              GitHub
            </a>
            <a
              href="https://www.linkedin.com/in/your-handle-here"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary underline-offset-2 hover:underline"
            >
              LinkedIn
            </a>
            <a
              href="https://dev.to/your-handle-here"
              target="_blank"
              rel="noreferrer"
              className="hover:text-primary underline-offset-2 hover:underline"
            >
              dev.to
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}
